import React from 'react';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import {
  ChevronLeftIcon,
  CustomerChatbotIcon,
  IngredientIcon,
  NoAttachmentIcon,
  ProductDefaultAttachmentIcon,
  ProductIcon,
  ProviderIcon,
  SearchChatHistoryIcon,
  SearchNoResultIcon,
  ShoppingBagIcon,
  StaffIcon,
  SupplierChatbotDefaultIcon,
} from '@assets/icon';
import type { ChatComponentItem, ComponentType } from '../../../../types/chat-component';
import { COMPONENT_TAB_META } from '../../../../hooks/useComponentPicker';

type ComponentIcon = React.FC<React.SVGProps<SVGSVGElement>>;

export const COMPONENT_TYPE_ICONS: Record<ComponentType, ComponentIcon> = {
  order: ShoppingBagIcon,
  product: ProductIcon,
  ingredient: IngredientIcon,
  customer: CustomerChatbotIcon,
  supplier: ProviderIcon,
  employee: StaffIcon,
};

export const ComponentTypeIcon: React.FC<{
  type: ComponentType;
  className?: string;
}> = ({ type, className = 'h-5 w-5' }) => {
  const Icon = COMPONENT_TYPE_ICONS[type];
  return <Icon className={className} />;
};

const PERSON_TYPES: ComponentType[] = ['customer', 'supplier', 'employee'];

const ItemAvatar: React.FC<{ item: ChatComponentItem }> = ({ item }) => {
  if (item.type === 'product') {
    return (
      <span className="inline-flex size-10 shrink-0 overflow-hidden rounded-[8px] bg-[#f7f7f8]">
        {item.avatarUrl ? (
          <img src={item.avatarUrl} alt="" className="size-full object-cover" />
        ) : (
          <ProductDefaultAttachmentIcon className="size-full" />
        )}
      </span>
    );
  }

  if (PERSON_TYPES.includes(item.type)) {
    return (
      <span className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f7f7f8] text-[#242428]">
        {item.avatarUrl ? (
          <img src={item.avatarUrl} alt="" className="size-full object-cover" />
        ) : (
          <SupplierChatbotDefaultIcon className="size-full" />
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f7f7f8] text-[#242428]">
      {item.avatarUrl ? (
        <img src={item.avatarUrl} alt="" className="size-full object-cover" />
      ) : (
        <ComponentTypeIcon type={item.type} className="h-5 w-5" />
      )}
    </span>
  );
};

const EMPTY_STATE_COPY: Record<ComponentType, { title: string; description: string }> = {
  order: {
    title: 'Bạn chưa có đơn hàng',
    description: 'Tạo đơn hàng trên Tendoo để tôi giúp bạn phân tích nhé!',
  },
  product: {
    title: 'Bạn chưa có sản phẩm',
    description:
      'Tạo sản phẩm trên Tendoo để tôi giúp bạn quản lý, phân tích, gợi ý nhé!',
  },
  ingredient: {
    title: 'Bạn chưa có nguyên liệu',
    description:
      'Tạo nguyên liệu trên Tendoo để tôi giúp bạn quản lý, phân tích, gợi ý nhé!',
  },
  customer: {
    title: 'Bạn chưa có khách hàng',
    description:
      'Tạo khách hàng trên Tendoo để tôi giúp bạn quản lý, phân tích và gợi ý doanh thu cho khách hàng nhé!',
  },
  supplier: {
    title: 'Bạn chưa có nhà cung cấp',
    description:
      'Tạo nhà cung cấp trên Tendoo để tôi giúp bạn quản lý và phân tích nhé!',
  },
  employee: {
    title: 'Bạn chưa có nhân viên',
    description:
      'Tạo nhân viên trên Tendoo để tôi giúp bạn quản lý và phân tích nhé!',
  },
};

interface ComponentPickerProps {
  allowedTypes: ComponentType[];
  selectedType: ComponentType;
  onSelectType: (type: ComponentType) => void;
  detailOpen: boolean;
  onOpenDetail: () => void;
  onBack: () => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  onSearchCommit: () => void;
  items: ChatComponentItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  onLoadMore: () => void;
  onPickItem: (item: ChatComponentItem) => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
}

const ComponentPicker: React.FC<ComponentPickerProps> = ({
  allowedTypes,
  selectedType,
  onSelectType,
  detailOpen,
  onOpenDetail,
  onBack,
  searchInput,
  onSearchChange,
  onSearchCommit,
  items,
  loading,
  loadingMore,
  error,
  onLoadMore,
  onPickItem,
  menuRef,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const selectedMeta = COMPONENT_TAB_META[selectedType];
  const emptyCopy = EMPTY_STATE_COPY[selectedType];

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || loadingMore) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= 40) onLoadMore();
  };

  const renderItem = (item: ChatComponentItem) => {
    const titleClampClass =
      item.type === 'product' || item.type === 'ingredient'
        ? 'line-clamp-2'
        : 'line-clamp-1';
    const title =
      item.type === 'order' ? (
        <>
          Đơn hàng <span className="font-semibold">{item.displayName}</span>
        </>
      ) : (
        item.displayName
      );

    return (
      <button
        key={`${item.type}-${item.id}`}
        type="button"
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[#242428] hover:bg-[rgba(61,61,67,0.04)]"
        onClick={() => onPickItem(item)}
      >
        <ItemAvatar item={item} />
        <span className="min-w-0 flex-1">
          <span className={`${titleClampClass} min-w-0 text-[15px] leading-6`}>{title}</span>
          {item.subtitle && (
            <span className="block truncate text-[12px] leading-4 text-[#8e8e93]">{item.subtitle}</span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div
      ref={menuRef}
      className="component-picker-menu absolute bottom-[calc(100%+12px)] left-0 right-0 z-[120] rounded-[24px] p-1"
    >
      <div className="max-h-[290px] overflow-hidden rounded-[20px] bg-white">
        <div
          className={`flex w-[200%] transition-transform duration-300 ease-out ${
            detailOpen ? '-translate-x-1/2' : 'translate-x-0'
          }`}
        >
          <div className="w-1/2 shrink-0">
            {allowedTypes.map((type) => (
              <button
                key={type}
                type="button"
                className="flex h-12 w-full items-center justify-between px-4 text-left text-[#242428] hover:bg-[rgba(61,61,67,0.04)]"
                onClick={() => {
                  onSelectType(type);
                  onOpenDetail();
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-5 w-5 items-center justify-center">
                    <ComponentTypeIcon type={type} />
                  </span>
                  <span className="text-[16px] font-medium leading-6">
                    {COMPONENT_TAB_META[type].label}
                  </span>
                </div>
                <RightOutlined className="text-xs text-[#5c5c66]" />
              </button>
            ))}
          </div>

          <div className="flex max-h-[290px] w-1/2 shrink-0 flex-col">
            <div className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-3">
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#242428] hover:bg-[rgba(61,61,67,0.06)]"
                onClick={onBack}
                aria-label="Quay lại"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-full border border-[#ececf0] bg-white px-3">
                <SearchChatHistoryIcon className="h-4 w-4 shrink-0 text-[#8e8e93]" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onSearchCommit();
                    }
                  }}
                  placeholder={`Tìm kiếm ${selectedMeta.label.toLowerCase()}`}
                  className="min-w-0 flex-1 border-0 bg-transparent text-[14px] leading-5 text-[#242428] outline-none placeholder:text-[#8e8e93]"
                />
              </div>
            </div>

            <div
              ref={listRef}
              onScroll={handleScroll}
              className="min-h-0 flex-1 overflow-y-auto pb-2"
            >
              {loading && items.length === 0 && (
                <div className="flex items-center justify-center gap-2 px-4 py-6 text-[14px] text-[#8e8e93]">
                  <LoadingOutlined />
                  <span>Đang tải...</span>
                </div>
              )}

              {!loading && error && items.length === 0 && (
                <div className="px-4 py-6 text-center text-[14px] text-[#8e8e93]">
                  {error}
                </div>
              )}

              {!loading && !error && items.length === 0 && (
                searchInput.trim() ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-4 py-6">
                    <SearchNoResultIcon className="h-[140px] w-[140px] shrink-0" />
                    <p className="m-0 text-center text-base font-medium leading-[22px] text-[#242428]">
                      Không có kết quả tìm kiếm
                    </p>
                    <p className="m-0 text-center text-xs font-normal leading-4 text-[#62626A]">
                      Thử từ khoá khác hoặc kiểm tra chính tả nhé
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
                    <NoAttachmentIcon className="h-[120px] w-[120px] shrink-0" />
                    <p className="m-0 text-base font-semibold leading-5 text-[#242428]">
                      {emptyCopy.title}
                    </p>
                    <p className="m-0 text-xs font-normal leading-[15px] text-[#8e8e93]">
                      {emptyCopy.description}
                    </p>
                  </div>
                )
              )}

              {items.map(renderItem)}

              {loadingMore && (
                <div className="flex items-center justify-center gap-2 px-4 py-3 text-[13px] text-[#8e8e93]">
                  <LoadingOutlined />
                  <span>Đang tải thêm...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentPicker;
